import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentPropsWithoutRef, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/utils'

type ButtonVariant = 'default' | 'primary' | 'agent' | 'ghost'
type ButtonSize = 'default' | 'sm'

interface BaseButtonProps {
  children: ReactNode
  className?: string
  variant?: ButtonVariant
  size?: ButtonSize
}

type NativeButtonProps = BaseButtonProps & ButtonHTMLAttributes<HTMLButtonElement> & {
  to?: never
  href?: never
}

type LinkButtonProps = BaseButtonProps & Omit<ComponentPropsWithoutRef<typeof Link>, 'className' | 'to'> & {
  to: string
  href?: never
}

type AnchorButtonProps = BaseButtonProps & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'href'> & {
  href: string
  to?: never
}

export type ButtonProps = NativeButtonProps | LinkButtonProps | AnchorButtonProps

function buttonClassName(variant: ButtonVariant, size: ButtonSize, className?: string) {
  return cn(
    'aw-button',
    variant !== 'default' && variant,
    size === 'sm' && 'sm',
    className,
  )
}

export function Button(props: ButtonProps) {
  if ('to' in props && props.to) {
    const { children, className, variant = 'default', size = 'default', to, ...anchorProps } = props
    return (
      <Link to={to} className={buttonClassName(variant, size, className)} {...anchorProps}>
        {children}
      </Link>
    )
  }

  if ('href' in props && props.href) {
    const { children, className, variant = 'default', size = 'default', href, ...anchorProps } = props
    return (
      <a href={href} className={buttonClassName(variant, size, className)} {...anchorProps}>
        {children}
      </a>
    )
  }

  const { children, className, variant = 'default', size = 'default', ...buttonProps } = props as NativeButtonProps
  return (
    <button className={buttonClassName(variant, size, className)} {...buttonProps}>
      {children}
    </button>
  )
}
